import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList,ViewChild, ViewChildren} from '@angular/core'
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { DataFilters } from 'src/app/models/DataFilters.model'
import {
  ASSET_CLASS_FILTERS,
  CREDIT_QUALITY_FILTERS,
  FILTER_MAP,
  INVESTMENT_MINIMUM_FILTERS,
  MANAGEMENT_STYLE_FILTERS,
  MARKET_CAPITALIZATION_FILTERS,
  MATURITY_FILTERS,
  PRODUCT_TYPE,
  REGION_FILTERS,
  SHARE_CLASS_FILTERS,
  STRATEGY_FILTERS,
  STRATEGY_FILTERS_529,
  STYLE_FILTERS,
  TAX_EFFICIENCY_FILTERS,
} from 'src/app/models/filter-details.model'
import { FiltersService } from 'src/app/services/filtersService.service'

import {FundDetailService} from "../../../services/fund-detail.service";

@Component({
  selector: 'filter-by',
  templateUrl: './filterBy.component.html',
  styleUrls: ['./filterBy.component.scss'],
})
export class FilterByComponent implements OnInit, OnDestroy {
  private resizeObserver: ResizeObserver;
  private eventListenersMap = new Map<HTMLElement, { focus: () => void; keydown: (e: KeyboardEvent) => void }>();

  public readonly assetClassFilterLabels = ASSET_CLASS_FILTERS
  public readonly regionFilterLabels = REGION_FILTERS
  public readonly marketSubFilterLabels = MARKET_CAPITALIZATION_FILTERS
  public readonly styleSubFilterLabels = STYLE_FILTERS
  public readonly creditQualityFilterLabels = CREDIT_QUALITY_FILTERS
  public readonly maturityFilterLabels = MATURITY_FILTERS
  public readonly managementStyleFilterLabels = MANAGEMENT_STYLE_FILTERS
  public readonly shareClassFilterLabels = SHARE_CLASS_FILTERS
  public readonly investmentMinimumFilters = INVESTMENT_MINIMUM_FILTERS
  public readonly taxEfficiencyFilterLabels = TAX_EFFICIENCY_FILTERS
  public readonly strategyFilterLabels = STRATEGY_FILTERS
  public readonly strategy529FilterLabels = STRATEGY_FILTERS_529
  public readonly productTypeFilterLabels = PRODUCT_TYPE
  public readonly filterMap = FILTER_MAP
  public activeFilters: DataFilters
  public productTypeGroup: UntypedFormGroup

  private destroy$ = new Subject<void>();

  
  @Input() isNonVanguard : boolean
  @Input() isMobileResolution;
  public readonly minimumsMap = {
    '1-1000': '$1 - $1,000',
    '1000-3000': '$1,000 - $3,000',
    '3000-10000': '$3,000 - $10,000',
    '10000-25000': '$10,000 - $25,000',
    '25000-50000': '$25,000 - $50,000',
    '50000>': '$50,000 >',
    closed: 'Closed funds',
  }
  public readonly TWO_ROW_HEIGHT = 72;

  fundFamily: any;
  fundFamilyName: string;
  fundFamilyId: number;
  hasLoadFunds: any;
  hasNTFFunds: any;
  fundFamilySelection: any;
  fund: any;
  showAllFilters: boolean = false;
  hasThirdRow: boolean = false;

  constructor(
    private readonly filtersService: FiltersService,
    public fundDetailService: FundDetailService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  @ViewChild('filterList') filterList!: ElementRef;
  @ViewChildren('filterChip') filterChips!: QueryList<ElementRef>;

  checkForThirdRow(): void {
    if (!this.filterList) return;
    const container: HTMLElement = this.filterList.nativeElement;
    const gap = 2;

    this.hasThirdRow = (container.scrollHeight - gap) > this.TWO_ROW_HEIGHT;

    this.cdr.detectChanges();
  }

  toggleShowAll(): void {
    this.showAllFilters = !this.showAllFilters;
    if (!this.showAllFilters) {
      this.filterList.nativeElement.style.maxHeight = `${this.TWO_ROW_HEIGHT}px`;
    } else {
      this.filterList.nativeElement.style.maxHeight = 'none'; 
    }

    setTimeout(() => this.manageFocusableElements(), 0);
  }

  public get isNonVanguardMutualFunds(): boolean {
    return this.filtersService.activeFilters.discoverBy === 'non-vanguard-mutual-funds'
  }

  ngOnInit() {
    this.productTypeGroup = this.filtersService.activeFilters.productType;
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const fundFamilyId = params['FundFamilyId'];

      this.activeFilters = this.filtersService.activeFilters

      if (fundFamilyId) {
        this.filtersService.FundFamilyId = fundFamilyId;
        this.getFamilyName();
      }
    
      this.checkForThirdRow()
    });

    this.productTypeGroup.valueChanges.subscribe(() => {
      this.checkForThirdRow() 
      setTimeout(() => this.manageFocusableElements(), 0);
     
    })

    for (const key in this.filtersService.activeFilters) {
      if (this.filtersService.activeFilters[key]?.valueChanges) {
        this.filtersService.activeFilters[key].valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.checkForThirdRow();
            setTimeout(() => this.manageFocusableElements(), 0);
          });
      }
    }
  }

  ngAfterViewInit() {
    if (this.filterList) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkForThirdRow();
        this.manageFocusableElements();
      });

      this.resizeObserver.observe(this.filterList.nativeElement);

      this.manageFocusableElements();
    }
  }

  private manageFocusableElements(): void {
    if (!this.filterList) return;

    this.cleanupEventListeners();

    const container: HTMLElement = this.filterList.nativeElement;
    const filterChips = container.querySelectorAll('.filter-item-chip') as NodeListOf<HTMLElement>;
    
    if (filterChips.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;
    
    if (!this.showAllFilters && this.hasThirdRow) {      
      this.manageCollapsedTabOrder(filterChips, containerBottom);
    } else {
      this.restoreNormalTabOrder(filterChips);
    }
  }

  private cleanupEventListeners(): void {
    this.eventListenersMap.forEach((listeners, element) => {
      element.removeEventListener('focus', listeners.focus);
      element.removeEventListener('keydown', listeners.keydown);
    });
    this.eventListenersMap.clear();
  }

  private findLastVisibleElement(filterChips: NodeListOf<HTMLElement>, containerBottom: number): HTMLElement | null {
    let lastVisibleElement: HTMLElement | null = null;
    
    filterChips.forEach((chip: HTMLElement) => {
      const chipRect = chip.getBoundingClientRect();
      const isVisible = chipRect.bottom <= containerBottom;
      
      if (isVisible) {
        lastVisibleElement = chip;
      }
    });

    return lastVisibleElement;
  }

  private updateLastVisibleElementTabNavigation(): void {
    if (!this.filterList) return;

    const container: HTMLElement = this.filterList.nativeElement;
    const filterChips = container.querySelectorAll('.filter-item-chip') as NodeListOf<HTMLElement>;
    
    if (filterChips.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;
    
    const lastVisibleElement = this.findLastVisibleElement(filterChips, containerBottom);
    
    this.eventListenersMap.forEach((listeners, element) => {
      element.removeEventListener('keydown', listeners.keydown);
    });
    
    if (lastVisibleElement && this.eventListenersMap.has(lastVisibleElement)) {
      const listeners = this.eventListenersMap.get(lastVisibleElement)!;
      lastVisibleElement.addEventListener('keydown', listeners.keydown);
    }
  }

  private manageCollapsedTabOrder(filterChips: NodeListOf<HTMLElement>, containerBottom: number): void {
    const HIDDEN_TAB_INDEX = -1;
    
    filterChips.forEach((chip: HTMLElement) => {
      const chipRect = chip.getBoundingClientRect();
      const isVisible = chipRect.bottom <= containerBottom;
      
      if (isVisible) {
        chip.tabIndex = 0;
      } else {
        chip.tabIndex = HIDDEN_TAB_INDEX;
      }
      
      const focusHandler = this.preventScrollOnFocus.bind(this);
      const keydownHandler = this.handleLastVisibleElementTab.bind(this);
      
      this.eventListenersMap.set(chip, {
        focus: focusHandler,
        keydown: keydownHandler
      });
      
      chip.addEventListener('focus', focusHandler, { passive: false });
    });

    this.updateLastVisibleElementTabNavigation();
  }

  private restoreNormalTabOrder(filterChips: NodeListOf<HTMLElement>): void {
    filterChips.forEach((chip: HTMLElement) => {
      chip.tabIndex = 0;
    });
  }

  private preventScrollOnFocus(): void {
    if (!this.filterList) return;
    
    const container = this.filterList.nativeElement;
    const currentScrollTop = container.scrollTop;
    
    setTimeout(() => {
      container.scrollTop = currentScrollTop;
    }, 0);
  }

  private handleLastVisibleElementTab(event: KeyboardEvent): void {
    if (event.key === 'Tab' && !event.shiftKey) {
      const buttons = document.querySelectorAll('see-more') as NodeListOf<HTMLElement>;
  
      if (buttons.length > 0) {
        event.preventDefault();
        buttons.forEach((button) => {
          button.focus();
        });
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupEventListeners();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getFamilyName(): void {
    this.fundDetailService.getfundFamily().pipe(take(1)).subscribe((response) => {
      this.fundFamily = response.fundFamily;

      const fundFamilyData = this.fundFamily.find(fund => Number(this.filtersService.FundFamilyId) === fund.fundFamilyId);

      if (fundFamilyData) {
        this.fundFamilyName = fundFamilyData.fundFamilyName;
        this.fundFamilyId = fundFamilyData.fundFamilyId;
        this.hasLoadFunds = fundFamilyData.hasLoadFunds;
        this.hasNTFFunds = fundFamilyData.hasNTFFunds;
        this.fundFamilySelection = `${this.fundFamilyName}${this.hasLoadFunds ? ' [Load]' : ''}${this.hasNTFFunds ? ' [NTF]' : ''} mutual funds`;

        this.activeFilters.fundFamily = {
          id: this.fundFamilyId,
          name: this.fundFamilySelection,
        };
      }
    });
  }

  cleanFundFamilyId() {
    this.filtersService.activeFilters.fundFamily = {}
    this.filtersService.activeFilters.discoverBy = 'mutual-funds';
    this.filtersService.applyFilters()
  }

  clearFilters(): void {
    this.filtersService.clearFilters()
    this.showAllFilters = false;
    
    setTimeout(() => this.updateLastVisibleElementTabNavigation(), 0);
  }

  removeDiscoverBy() {
    this.activeFilters.discoverBy = 'all'
    this.filtersService.applyFilters()
  }

  removeFromArray(array, value): void {
    this.checkForThirdRow();
    array.splice(array.indexOf(value), 1)
    this.filtersService.applyFilters()
    
    setTimeout(() => this.updateLastVisibleElementTabNavigation(), 0);
  }

  // eslint-disable-next-line complexity
  areAllFiltersEmpty(): boolean {
    const isRiskEmpty = this.activeFilters.risk.length === 0
    const isAssetClassEmpty =
      ASSET_CLASS_FILTERS.filter(
        (value) => this.activeFilters.assetClass.controls[value].value,
      ).length <= 0

    const isShareClassArrayEmpty =
      SHARE_CLASS_FILTERS.filter(
        (value) => this.activeFilters.shareClass.controls[value].value,
      ).length <= 0
    const istaxEfficiencyArrayEmpty =
      TAX_EFFICIENCY_FILTERS.filter(
        (value) => this.activeFilters.taxEfficiency.controls[value].value,
      ).length <= 0
    const isManagementStyleArrayEmpty =
      MANAGEMENT_STYLE_FILTERS.filter(
        (value) => this.activeFilters.managementStyle.controls[value].value,
      ).length <= 0
    const isInvestmentMinimumsArrayEmpty =
      INVESTMENT_MINIMUM_FILTERS.filter(
        (value) => this.activeFilters.investmentMinimums.controls[value].value,
      ).length <= 0
    const isStrategyEmpty = STRATEGY_FILTERS.filter(
      (value) => this.activeFilters.strategy.controls[value].value,
    ).length <= 0
    const isStrategy529Empty =STRATEGY_FILTERS_529.filter(
      (value) => this.activeFilters.strategy529.controls[value].value,
    ).length <= 0
    const isProductTypeEmpty = PRODUCT_TYPE.filter(
      (value) => this.activeFilters.productType.controls[value].value,
    ).length <= 0
    const isRegionEmpty = REGION_FILTERS.filter(
      (value) => this.activeFilters.region.controls[value].value,
    ).length <= 0
    return (
      isRiskEmpty &&
      isAssetClassEmpty &&
      isManagementStyleArrayEmpty &&
      isShareClassArrayEmpty &&
      isInvestmentMinimumsArrayEmpty &&
      isStrategyEmpty &&
      istaxEfficiencyArrayEmpty &&
      isProductTypeEmpty &&
      isStrategy529Empty &&
      isRegionEmpty
    )
  }

  clearAssetClass(label) {
    this.activeFilters.assetClass.controls[label].setValue(false)
    if (label === 'equity') {
      this.setValueForMarketStyle(false)
    }
    if (label === 'fixed_income') {
      this.setValueForCreditMaturity(false)
    }
    
    setTimeout(() => this.updateLastVisibleElementTabNavigation(), 0);
  }

  setValueForMarketStyle(value) {
    MARKET_CAPITALIZATION_FILTERS.forEach((market) => {
      this.activeFilters.assetClassMarketCap['equity'].controls[
        market
      ].setValue(value)
    })
    STYLE_FILTERS.forEach((style) => {
      this.activeFilters.assetClassStyle['equity'].controls[style].setValue(
        value,
      )
    })
  }
  setValueForCreditMaturity(value) {
    CREDIT_QUALITY_FILTERS.forEach((credit) => {
      this.activeFilters.assetClassCredit['fixed_income'].controls[
        credit
      ].setValue(value)
    })
    MATURITY_FILTERS.forEach((maturity) => {
      this.activeFilters.assetClassMaturity['fixed_income'].controls[
        maturity
      ].setValue(value)
    })
  }

  value: string;
  // eslint-disable-next-line complexity
  formatFilterValue(val) {
    if (val === 'sector_specialty') {
      return val.replace(/_/g, ' & ')
    }
    if (val === 'esg') {
      return 'ESG'
    } else if(val==="equity"){
      return 'Stocks'
    } else if(val==="multi_asset"){
      return 'Balanced'
    }
    if (val === 'total_market_etfs') {
      return 'Total market ETFs'
    }
    if (val === 'etfs') {
      return 'Vanguard ETFs'
    }
    if (val === 'mutual-funds') {
      return 'Vanguard mutual funds'
    }
    if (val === 'us') {
      return 'US'
    } else {
      return val.replace(/_/g, ' ')
    }
  }
}
